/**
 * Small in-memory cache of decrypted attachment Blobs, so media the user has
 * ALREADY loaded this session can be rendered on the very first paint.
 *
 * Why it exists: `processMediaAttachment` is async even on a warm cache (an
 * IndexedDB/OPFS read), and the loading state blanks the blob before it starts.
 * So a video you had already watched still went blank → poster → video, and an
 * image still went blur → sharp, purely because the render couldn't know
 * synchronously that the bytes were already at hand. A synchronous lookup
 * removes that phase entirely (and makes swiping back and forth instant).
 *
 * Deliberate design choices:
 *
 * - **Blobs, not object URLs.** Evicting a URL would break any component still
 *   showing it. A Blob keeps whatever URL a component already created alive, so
 *   eviction can never produce a broken image.
 * - **Bounded by bytes AND count, LRU.** Decrypted media is large; an unbounded
 *   map is a memory leak on mobile.
 * - **Oversized items are skipped.** One big video would evict everything else,
 *   and it's exactly the case the poster/spinner exists for anyway.
 * - **Session-only.** IndexedDB/OPFS remains the durable cache; this is purely
 *   a first-paint accelerator and is safe to drop at any time.
 */

const MAX_ENTRIES = 12;
const MAX_TOTAL_BYTES = 100 * 1024 * 1024; // 100 MiB across all entries
const MAX_ENTRY_BYTES = 20 * 1024 * 1024; // skip anything larger

/** Per-entry ceiling, exported so callers can avoid work whose result this
 *  cache would refuse anyway (e.g. the viewer's neighbour prefetch). */
export const MAX_CACHEABLE_ENTRY_BYTES = MAX_ENTRY_BYTES;

// Map preserves insertion order, which is all an LRU needs: delete + re-set on
// read moves an entry to the most-recent end.
const entries = new Map<string, Blob>();
let totalBytes = 0;

/** Cache key. `field` separates a thumbnail from the full content of one id. */
export function mediaCacheKey(
  attachmentId: string,
  field: "content" | "thumbnail",
): string {
  return `${attachmentId}:${field}`;
}

/** Synchronous read that marks the entry as recently used. */
export function getCachedMedia(key: string): Blob | null {
  const blob = entries.get(key);
  if (!blob) {
    return null;
  }
  entries.delete(key);
  entries.set(key, blob);
  return blob;
}

/**
 * Synchronous read WITHOUT touching the LRU order — safe to call during the
 * React render phase (render must be side-effect-free; reordering the Map is a
 * mutation). The accompanying effect's `getCachedMedia` call performs the
 * recency touch after commit.
 */
export function peekCachedMedia(key: string): Blob | null {
  return entries.get(key) ?? null;
}

/** Store a decrypted blob, evicting least-recently-used entries as needed. */
export function setCachedMedia(key: string, blob: Blob): void {
  if (!blob || blob.size === 0 || blob.size > MAX_ENTRY_BYTES) {
    return;
  }
  const existing = entries.get(key);
  if (existing) {
    totalBytes -= existing.size;
    entries.delete(key);
  }
  entries.set(key, blob);
  totalBytes += blob.size;

  // Evict from the least-recently-used end until back inside both budgets.
  for (const [oldestKey, oldest] of entries) {
    if (entries.size <= MAX_ENTRIES && totalBytes <= MAX_TOTAL_BYTES) {
      break;
    }
    if (oldestKey === key) {
      continue; // never evict the entry we just stored
    }
    entries.delete(oldestKey);
    totalBytes -= oldest.size;
  }
}

/** Drop an entry (e.g. its content was replaced). */
export function deleteCachedMedia(key: string): void {
  const blob = entries.get(key);
  if (blob) {
    totalBytes -= blob.size;
    entries.delete(key);
  }
}

/** Clear everything — used by storage settings and tests. */
export function clearCachedMedia(): void {
  entries.clear();
  totalBytes = 0;
}

/** Diagnostics (tests + storage settings). */
export function mediaCacheStats(): { count: number; bytes: number } {
  return { count: entries.size, bytes: totalBytes };
}
